import axios from 'axios';

import {getAdResponse} from '@jobseeker/common-core/utils';
import type {AdsElementType, AdsOptions} from '@jobseeker/common-core/types';
import courseService from '@jobseeker/common-core/services/course.service';

const sortByOrder = (ads: AdsElementType[]) => {
  if (ads && ads.some(adElement => 'ordering' in adElement)) {
    return ads.sort(
      (a: any, b: any) => parseInt(a.ordering, 10) - parseInt(b.ordering, 10)
    );
  }
  return ads;
};

const buildCourseParams = (options?: AdsOptions): any => {
  const params: any = {
    imageType: 'webp',
    ...(options?.courseId && {courseId: options.courseId}),
  };

  if (options?.categoryIds && options.categoryIds !== '0') {
    params['categoryIds'] =
      options.categoryIds === 'jobfeed' ? '' : options.categoryIds;
  }

  if (options?.page !== undefined) {
    params['page'] = options.page;
  }

  if (options?.size) {
    params['size'] = options.size;
  }

  if (options?.shuffle) {
    params['shuffle'] = options.shuffle;
  }

  if (options?.orderby) {
    params['orderby'] = options.orderby;
  }

  if (options?.courseTags) {
    params['courseTags'] = options.courseTags;
  }

  return params;
};

const processAdList = (
  adList: any[],
  options?: AdsOptions
): AdsElementType[] => {
  if (!adList || adList.length === 0) {
    return [];
  }

  let adListFinal = adList;

  if (options?.categoryIds === 'jobfeed') {
    adListFinal = adList.filter(
      (j: any) => j?.metadata[0]?.['Allowed on JobFeeds'] === 1
    );
  }

  if (options?.onRotation) {
    if (adListFinal.length > 1) {
      const currentDate = new Date();
      const day = currentDate.getDate();
      const index = day % 2 === 0 ? 1 : 0;
      adListFinal = [adListFinal[index]];
    }
  }

  let allAds: any = [];
  adListFinal.forEach(({metadata, id, categoryIds}: any) => {
    if (metadata?.length > 0) {
      metadata[0]['id'] = id;
    }
    allAds = allAds.concat(getAdResponse(metadata, categoryIds));
  });

  return sortByOrder(allAds);
};

const fetchAndProcessCourseAds = async (
  apiCall: () => Promise<any>,
  options?: AdsOptions,
  returnEntireResponse = false,
  errorContext = 'courses-list'
) => {
  try {
    const response = await apiCall();

    if (returnEntireResponse) {
      return response;
    }

    const adList = response.data?.adList;
    return processAdList(adList || [], options);
  } catch (error) {
    console.log(
      `🚀 ~ file: loadAds.ts ~ loadAds ~ ${errorContext} error:`,
      error
    );
    return '';
  }
};

const PLATFORM = process?.env?.NEXT_PUBLIC_APP_NAME ?? '';

const AD_SLOTS_CONFIG = {
  iimjobs: {
    learnGrow: {
      slot: '5eb98a896162be262c4018d8',
      apiCall: courseService.getCoursesList,
      context: 'courses-list',
    },
    featuredInstitutes: {
      slot: '650415c2217aa8a716c95671',
      apiCall: courseService.getFeaturedInstitutes,
      context: 'featured-institutes',
    },
    suggestedCourses: {
      slot: '65ba0f7441481ab46d4dc118',
      apiCall: courseService.getCoursesAfterJobApply,
      context: 'job-ready-courses',
    },
    infeedCategory: {
      slot: '65142140fdf3600902beb48b',
      apiCall: courseService.getInFeedCourses,
      context: 'in-feed-courses',
    },
    inFeed: {
      slot: '651421837bcf98cd3e3f93fe',
      apiCall: courseService.getInFeedCourses,
      context: 'in-feed-courses',
    },
    trendingCoursesInSearchPopup: {
      slot: '60291db8faac1c41da1fd99b',
      apiCall: courseService.getCoursesByTags,
      context: 'trending-courses-in-search-popup',
    },
  },
  hirist: {
    learnGrow: {
      slot: '5d5a557256da611cc6fa1269',
      apiCall: courseService.getCoursesList,
      context: 'courses-list',
    },
    featuredInstitutes: {
      slot: '5d5a553056da611cc6fa1267',
      apiCall: courseService.getFeaturedInstitutes,
      context: 'featured-institutes',
    },
    suggestedCourses: {
      slot: '5d95cda317165c5ca17f0781',
      apiCall: courseService.getCoursesAfterJobApply,
      context: 'job-ready-courses',
    },
    infeedCategory: {
      slot: '5d5a534156da611cc6fa1264',
      apiCall: courseService.getInFeedCourses,
      context: 'in-feed-courses',
    },
    inFeed: {
      slot: '5d5a531656da611cc6fa1263',
      apiCall: courseService.getInFeedCourses,
      context: 'in-feed-courses',
    },
    trendingCoursesInSearchPopup: {
      slot: '5d665a612d5fbc4e3909c567',
      apiCall: courseService.getCoursesByTags,
      context: 'trending-courses-in-search-popup',
    },
  },
};

const getCourseSlotConfig = () => {
  const config: Record<
    string,
    {
      apiCall: (params: any) => Promise<any>;
      context: string;
    }
  > = {};

  // Use platform-specific constant config
  const platformConfig =
    AD_SLOTS_CONFIG[PLATFORM as keyof typeof AD_SLOTS_CONFIG] ||
    AD_SLOTS_CONFIG.iimjobs;
  Object.values(platformConfig).forEach(({slot, apiCall, context}) => {
    config[slot] = {apiCall, context};
  });

  return config;
};

const loadAds = async (
  slotId: string,
  options?: AdsOptions,
  isCourseAds: boolean = false,
  returnEntireResponse = false
) => {
  if (typeof window === 'undefined') {
    return [];
  }

  const courseSlotConfigMap = getCourseSlotConfig();
  const courseSlotConfig = courseSlotConfigMap[slotId];

  if (courseSlotConfig) {
    const params = buildCourseParams(options);
    return fetchAndProcessCourseAds(
      () => courseSlotConfig.apiCall({params}),
      options,
      returnEntireResponse,
      courseSlotConfig.context
    );
  }

  let API_PATH = process.env.NEXT_PUBLIC_API_ADS;
  if (isCourseAds) {
    API_PATH = API_PATH + 'courses/';
  }

  const params: AdsOptions = {
    imageType: 'webp',
    ...(options?.courseId && {courseId: options.courseId}),
  };

  if (options?.categoryIds) {
    params['categoryIds'] =
      options.categoryIds === 'jobfeed' ? '' : options.categoryIds;
  }

  if (options?.page) {
    params['page'] = options.page;
  }

  if (options?.size) {
    params['size'] = options.size;
  }

  if (options?.exp) {
    params['exp'] = options.exp;
  }

  if (options?.gender) {
    params['gender'] = options.gender;
  }

  if (options?.functionalArea) {
    params['functionalArea'] = options.functionalArea;
  }

  if (options?.user_location) {
    params['user_location'] = options.user_location;
  }

  if (options?.tags) {
    params['tags'] = options.tags;
  }

  if (options?.shuffle) {
    params['shuffle'] = options.shuffle;
  }

  if (options?.orderby) {
    params['orderby'] = options.orderby;
  }

  if (options?.jobTags) {
    params['jobTags'] = options.jobTags;
  }

  if (options?.courseTags) {
    params['courseTags'] = options.courseTags;
  }

  try {
    const response = await axios.get(`${API_PATH}${slotId}`, {
      params: params,
    });
    if (returnEntireResponse) {
      return response?.data;
    }
    const adList = response.data?.data?.adList;
    return processAdList(adList || [], options);
  } catch (error) {
    console.log('🚀 ~ file: loadAds.ts:27 ~ loadAds ~ error:', error);
    return '';
  }
};

export const loadAdsServer = async (slotId: string) => {
  const API_PATH = process.env.NEXT_PUBLIC_API_ADS;
  try {
    const response = await axios.get(`${API_PATH}${slotId}`, {
      params: {imageType: 'webp'},
    });
    const adList = response.data?.data?.adList;
    return processAdList(adList || []);
  } catch (error) {
    console.log('🚀 ~ loadAdsServer ~ error:', error);
    return [];
  }
};

export default loadAds;
